import React, { useState, useMemo } from "react";
import { Button } from "./ui/button";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Label } from "./ui/label";
import { CheckCircle2, XCircle, RefreshCw, Award } from "lucide-react";
import { useApp } from "../context/AppContext";

export default function Quiz({ module: mod, onFinished }) {
  const { saveQuizResult, progress } = useApp();
  const existing = progress?.quiz_scores?.[mod.id];

  const [answers, setAnswers] = useState(() =>
    Array(mod.quiz.length).fill(null)
  );
  const [submitted, setSubmitted] = useState(false);
  const [retakeCount, setRetakeCount] = useState(0);

  const score = useMemo(() => {
    if (!submitted) return 0;
    let s = 0;
    answers.forEach((a, i) => {
      if (a === mod.quiz[i].correct) s++;
    });
    return s;
  }, [submitted, answers, mod.quiz]);

  const pct = submitted ? Math.round((score / mod.quiz.length) * 100) : 0;
  const passed = submitted && pct >= 60;

  const handleSubmit = () => {
    if (answers.some((a) => a === null)) return;
    
    // Calculate the score synchronously right now
    let calculatedScore = 0;
    answers.forEach((a, i) => {
      if (a === mod.quiz[i].correct) calculatedScore++;
    });

    setSubmitted(true);
    
    const calculatedPercent = Math.round((calculatedScore / mod.quiz.length) * 100);
    const isPassed = calculatedPercent >= 60;
    
    const result = {
      score: calculatedScore,
      total: mod.quiz.length,
      percent: calculatedPercent,
      passed: isPassed,
      at: new Date().toISOString(),
    };
    saveQuizResult(mod.id, result);
  };

  const handleRetake = () => {
    setAnswers(Array(mod.quiz.length).fill(null));
    setSubmitted(false);
    setRetakeCount(c => c + 1);
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid={`quiz-${mod.id}`}>
      <div className="mb-6">
        <div className="mono text-[11px] uppercase tracking-widest text-muted-foreground">
          Module {mod.number} Assessment
        </div>
        <h1 className="serif text-4xl sm:text-5xl mt-2">Quiz — {mod.title}</h1>
        <p className="text-muted-foreground mt-2">
          Pass with 60% or higher to complete this module. {existing?.passed && "(Previously passed)"}
        </p>
      </div>

      <div className="space-y-6" key={retakeCount}>
        {mod.quiz.map((q, qi) => {
          const userAnswer = answers[qi];
          const isCorrect = submitted && userAnswer === q.correct;
          const isWrong = submitted && userAnswer !== q.correct && userAnswer !== null;

          return (
            <div
              key={qi}
              className={`rounded-md border p-5 transition-colors ${
                submitted
                  ? isCorrect
                    ? "border-emerald-500/50 bg-emerald-50 dark:bg-emerald-950/30"
                    : "border-destructive/50 bg-destructive/5"
                  : "border-border bg-card"
              }`}
              data-testid={`quiz-question-${qi}`}
            >
              <div className="flex items-start gap-3 mb-4">
                <span className="mono text-xs bg-primary text-primary-foreground rounded-full w-7 h-7 inline-flex items-center justify-center shrink-0">
                  {qi + 1}
                </span>
                <p className="font-medium text-lg leading-snug">{q.q}</p>
              </div>

              <RadioGroup
                value={userAnswer === null ? undefined : String(userAnswer)}
                onValueChange={(v) => {
                  if (submitted) return;
                  const next = [...answers];
                  next[qi] = Number(v);
                  setAnswers(next);
                }}
                disabled={submitted}
                className="space-y-2"
              >
                {q.options.map((opt, oi) => {
                  const selected = userAnswer === oi;
                  const correctOne = submitted && oi === q.correct;
                  const wrongPick = submitted && selected && oi !== q.correct;
                  return (
                    <div
                      key={oi}
                      className={`flex items-center gap-3 rounded-md border px-3 py-2 ${
                        correctOne
                          ? "border-emerald-500 bg-emerald-100/50 dark:bg-emerald-900/40"
                          : wrongPick
                          ? "border-destructive bg-destructive/10"
                          : "border-border"
                      }`}
                    >
                      <RadioGroupItem
                        value={String(oi)}
                        id={`q${qi}-o${oi}`}
                        data-testid={`quiz-option-${qi}-${oi}`}
                      />
                      <Label htmlFor={`q${qi}-o${oi}`} className="cursor-pointer flex-1 text-base">
                        {opt}
                      </Label>
                      {correctOne && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                      {wrongPick && <XCircle className="w-4 h-4 text-destructive" />}
                    </div>
                  );
                })}
              </RadioGroup>

              {submitted && (
                <div className="mt-3 text-sm border-t border-border pt-3">
                  <span className="mono text-xs uppercase tracking-widest text-muted-foreground">
                    Explanation
                  </span>
                  <p className="mt-1">{q.explanation}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!submitted ? (
        <Button
          className="mt-8 w-full sm:w-auto"
          size="lg"
          onClick={handleSubmit}
          disabled={answers.some((a) => a === null)}
          data-testid="quiz-submit-btn"
        >
          Submit Quiz
        </Button>
      ) : (
        <div
          className={`mt-8 rounded-md border p-6 ${
            passed ? "border-emerald-500/50 bg-emerald-50 dark:bg-emerald-950/30" : "border-destructive/50 bg-destructive/5"
          }`}
          data-testid="quiz-result"
        >
          <div className="flex items-center gap-3 mb-2">
            {passed ? (
              <Award className="w-7 h-7 text-emerald-600" />
            ) : (
              <XCircle className="w-7 h-7 text-destructive" />
            )}
            <h2 className="serif text-3xl">
              {passed ? "Module passed!" : "Not quite — try again"}
            </h2>
          </div>
          <p className="text-base">
            You scored <span className="mono font-bold">{score}/{mod.quiz.length}</span> ({pct}%).{" "}
            {passed
              ? "This module is now marked complete."
              : "You need 60% to pass. Review the material and retake."}
          </p>

          <div className="flex flex-wrap gap-3 mt-4">
            <Button variant="outline" onClick={handleRetake} data-testid="quiz-retake-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Retake Quiz
            </Button>
            <Button onClick={onFinished} data-testid="quiz-back-home-btn">
              Back to Home
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
